import type { SerializedConstructor } from '@langchain/core/load/serializable';

import { parseLangSmithParamsFromRun } from '@/utils/langsmith-params';
import { filterAndSnakeCaseKeys } from '@/utils/stringUtils';

import type { ManifestPreviewProps } from '../../Manifest.utils';

export const MODELS = [
  'gpt-5',
  'gpt-5-mini',
  'gpt-5-nano',
  'o4-mini',
  'o1-mini',
  'o1-preview',
  'o1',
  'o3-mini',
  'o3',
  'gpt-4.1',
  'gpt-4.1-mini',
  'gpt-4.1-nano',
  'gpt-4o',
  'gpt-4o-mini',
  'gpt-4',
  'gpt-4-turbo',
  'gpt-4-0314',
  'gpt-4-0613',
  'gpt-4-32k',
  'gpt-4-32k-0314',
  'gpt-4-32k-0613',
  'gpt-4-turbo-preview',
  'gpt-4-1106-preview',
  'gpt-4-0125-preview',
  'gpt-4-vision-preview',
  'gpt-3.5-turbo',
  'gpt-3.5-turbo-0301',
  'gpt-3.5-turbo-0613',
  'gpt-3.5-turbo-1106',
  'gpt-3.5-turbo-0125',
  'gpt-3.5-turbo-16k',
  'gpt-3.5-turbo-16k-0613',
].map((value) => ({ label: value, value }));

export const DEFAULT_MODEL = 'gpt-5-mini';

const O1_MODEL_PREFIX = 'o1';
const O4_MODEL_PREFIX = 'o4';
const O3_MODEL_PREFIX = 'o3';
const GPT_5_MODEL_PREFIX = 'gpt-5';

/**
 * Note: Prefer gating on specific model capabilities
 * using getOpenAIModelCapabilities instead
 */
export const isAnyOSeriesModel = (
  model?: string
): {
  isO1Generic: boolean;
  isO1: boolean;
  isO3Generic: boolean;
  isO4Generic: boolean;
} => {
  const isO1Generic = !!model?.startsWith(O1_MODEL_PREFIX);
  const isO1 = model === O1_MODEL_PREFIX;
  const isO3Generic = !!model?.startsWith(O3_MODEL_PREFIX);
  const isO4Generic = !!model?.startsWith(O4_MODEL_PREFIX);
  return {
    isO1Generic,
    isO1,
    isO3Generic,
    isO4Generic,
  };
};

export const getOpenAIModelCapabilities = (model?: string) => {
  const { isO1Generic, isO1, isO3Generic, isO4Generic } =
    isAnyOSeriesModel(model);
  const isGPT5Generic = !!model?.startsWith(GPT_5_MODEL_PREFIX);

  // no o-series models support temperature
  const isTemperatureSupported =
    !isO1Generic && !isO3Generic && !isO4Generic && !isGPT5Generic;
  // o1-mini does not support reasoning effort
  const isReasoningEffortSupported =
    isO1 || isO3Generic || isO4Generic || isGPT5Generic;
  const isMinimalReasoningEffortSupported =
    isReasoningEffortSupported && (isO1 || isGPT5Generic);
  // no o1 models support system messages
  const isSystemMessagesSupported = !isO1Generic;
  // o1 does not support streaming
  const isStreamingSupported = !isO1;
  return {
    isTemperatureSupported,
    isReasoningEffortSupported,
    isMinimalReasoningEffortSupported,
    isSystemMessagesSupported,
    isStreamingSupported,
  };
};

export function useChatOpenAIModelAndProvider(
  props: ManifestPreviewProps<SerializedConstructor>
) {
  const id = props.value.id.at(-1) ?? '';
  const kwargs = props.value.kwargs;
  const model = kwargs.model ?? kwargs.model_name ?? DEFAULT_MODEL;

  return {
    model,
    provider: id,
  };
}

export function inferChatOpenAIManifestFromRun(
  params: ReturnType<typeof parseLangSmithParamsFromRun>,
  serializedModel: SerializedConstructor | null,
  isCustomModelEndpoint = false
): SerializedConstructor | null {
  if (
    params?.ls_provider !== 'openai' ||
    !(params?.ls_model_type == null || params.ls_model_type === 'chat') ||
    // Bug in LangChain JS means some old versions of fireworks set their ls_provider to 'openai'.
    (Array.isArray(serializedModel?.id) &&
      serializedModel?.id.at(-1) === 'ChatFireworks') ||
    // If it has a base url, only return true if we're checking for the CustomModelEndpoint
    (!isCustomModelEndpoint && !!serializedModel?.kwargs?.openai_api_base)
  ) {
    return null;
  }

  // Unpack LangChain JS configuration kwarg since LangChain PY doesn't support.
  const allowedConfigurations = [
    'organization',
    'baseURL',
    'timeout',
    'maxRetries',
    'defaultHeaders',
    'defaultQuery',
  ];
  const configuration = serializedModel?.kwargs?.configuration ?? {};

  return {
    lc: 1,
    type: 'constructor',
    id: isCustomModelEndpoint
      ? ['langsmith', 'playground', 'CustomModelEndpoint']
      : ['langchain', 'chat_models', 'openai', 'ChatOpenAI'],
    kwargs: {
      temperature: params.ls_temperature ?? 1,
      ...(params.ls_model_name !== undefined && {
        model: params.ls_model_name,
        model_name: undefined,
      }),
      ...(params.ls_max_tokens !== undefined && {
        max_tokens: params.ls_max_tokens,
      }),
      ...(serializedModel?.kwargs?.openai_api_base !== undefined && {
        base_url: serializedModel?.kwargs?.openai_api_base,
      }),
      ...filterAndSnakeCaseKeys(configuration, allowedConfigurations),
      http_client: undefined,
      http_async_client: undefined,
      configuration: undefined,
      stream_usage: true,
    },
  };
}
